import VueCookie from 'vue-cookie'
import { ActionTree } from 'vuex'
import { LangState } from './types'
import { RootState } from '../types'
import { asyncForEach, isEmpty } from '@/helpers/utils'
import config from 'config'
import {
  firstSegment,
  isSupportedLocale,
  isSupportedLanguage,
  getLocaleFromLanguage,
  getUrlParameter,
  normalizeLocaleFormat,
  getSupportedFromLocale
} from '@/helpers/url'
import { Logger } from '@/lib/logger'

export const actions: ActionTree<LangState, RootState> = {
  /**
   * Dispatch loadLang action for each prefixes
   * @param prefixes
   */
  async loadLangs ({ commit, dispatch, state }, prefixes: string[]) {
    await asyncForEach(prefixes, async (prefix: string) => {
      Logger.groupCollapsed(`Load lang: ${prefix}`)
      const translation = await dispatch('_loadLang', { prefix, locale: state.locale })
      if (!isEmpty(translation)) {
        commit('SET_TRANSLATION', {
          [prefix]: translation
        })
      } else {
        const fallbackTranslation = await dispatch('_loadLang', { prefix, locale: config.i18n.fallback_locale })
        commit('SET_TRANSLATION', {
          [prefix]: fallbackTranslation
        })
      }
      Logger.groupEnd()
    })
  },

  /**
   * Return lang file based on prefix and bestLocale asynchronously
   */
  async _loadLang (context, { prefix = null, locale = null }) {
    const supportedLocales: string[] = getSupportedFromLocale(locale)
    let hasFoundTranslation = false
    let translations = {}
    await asyncForEach(supportedLocales, async (locale: string) => {
      if (!hasFoundTranslation) {
        try {
          const response = await fetch(`/lang/${locale}/${prefix}.json`)
          translations = await response.json()
          hasFoundTranslation = true
          Logger.info(`Load ${prefix} in ${locale}`, 'i18n', `/lang/${locale}/${prefix}.json`, 'teal')()
        } catch (error) {
          Logger.warn(`Fail to load ${prefix} in ${locale}`, 'i18n', `/lang/${locale}/${prefix}.json`, 'teal')()
        }
      }
    })
    return translations
  },

  async setBestLocale ({ commit, state }) {
    const queryLocale: string | null = getUrlParameter(config.i18n.query_param)
    const urlLocale: string = firstSegment(window.location.pathname)
    const cookieLocale: string | undefined = VueCookie.get(config.i18n.cookie_locale)
    let browserLocale: string | null = normalizeLocaleFormat(window.navigator.language)

    /**
     *  if we have the query parameter 'reg_locale' from the app
     *    if we support his format (both locale and lang)
     *      we set the base URL (but not the cookie)
     *    if the first segment of the url is a supported locale
     *      set it as base Url
     *      if the cookie locale is different from the first segent locale
     *        overwrite the cookie locale to set it as the first segment locale
     *    else (if we don't have a supported locale as first segment)
     *      if we have a cookie locale
     *        set it as base URL locale
     *      else (if we don't have a cookie locale)
     *        if the browser locale is supported
     *          set it as base url
     *          set the cookie locale
     *        else if the language of the browser locale is supported. eg: if browser locale is 'fr'
     *          get the locale based on the language
     *          set the base url with that locale
     *          set the cookie based on that locale
     */
    if (queryLocale) {
      if (isSupportedLocale(queryLocale)) {
        await commit('SET_LOCALE', normalizeLocaleFormat(queryLocale))
      } else if (isSupportedLanguage(queryLocale)) {
        await commit('SET_LOCALE', normalizeLocaleFormat(queryLocale))
      } else {
        await commit('SET_LOCALE', state.locale)
      }
    } else {
      if (isSupportedLocale(urlLocale)) {
        await commit('SET_LOCALE', urlLocale)
        if (cookieLocale !== urlLocale) {
          VueCookie.set(config.i18n.cookie_locale, urlLocale)
        }
      } else {
        if (cookieLocale) {
          await commit('SET_LOCALE', cookieLocale)
        } else {
          if (isSupportedLocale(browserLocale)) {
            await commit('SET_LOCALE', browserLocale)
            VueCookie.set(config.i18n.cookie_locale, browserLocale)
          } else if (isSupportedLanguage(browserLocale)) {
            browserLocale = getLocaleFromLanguage(browserLocale)
            await commit('SET_LOCALE', browserLocale)
            VueCookie.set(config.i18n.cookie_locale, browserLocale)
          } else {
            await commit('SET_LOCALE', state.locale)
            VueCookie.set(config.i18n.cookie_locale, state.locale)
          }
        }
      }
    }
  }
}
